/*
 * Copyright (c) 2026.
 * # Autor: TECINCO TECNOLOGIA
 * # Dev: Alejandro Meriño Rodrìguez
 * # Versión: 2.0.0
 */

import {inject, Injectable} from "@angular/core";
import {Client} from "@interface/client";
import {firstValueFrom} from "rxjs";
import {map, timeout} from "rxjs/operators";
import {ClientService} from "@serv/client.service";
import {Tools_aiConsoleService} from "@llm/service/tools_ai-console.service";
import {LlmLoggingService} from "@llm/service/llm-logging.service";
import {PropertiesDialogDataSelect} from "@llm/service/utils_main_comp_variables.service";
import {ToolsAiDialogsService} from "@llm/service/tools_ai-dialogs.service";
import {ClientConfigFactory, ConfigFactory, Configuration, OsBudgetConfigFactory} from '@llm/factories/config.factory';
import {MkgOS} from "@app/class/mkg-os";
import {MkgOSPart} from "@app/class/mkg-os-part";




@Injectable({providedIn: 'root'})
export class UtilsMiscellaneousService {

    private _clientService = inject(ClientService);
    private _console = inject(Tools_aiConsoleService);
    private _llmLoggingService = inject(LlmLoggingService);
    private _toolsAiDialogsService = inject(ToolsAiDialogsService);



    async search_and_select_client(name: string): Promise<Client | undefined> {
        if (!name || name.trim() === '') {
            this._console.warn('search_and_select_client: Nombre de cliente vacío o inválido');
            return undefined;
        }

        const trimmedName = name.trim();

        try {
            const foundClients = await this.findClientInDb(trimmedName);

            if (!foundClients || foundClients.length === 0) {
                this._console.warn(`search_and_select_client: No se encontraron clientes para "${trimmedName}"`);
                return undefined;
            }

            const selectedClient: Client = await this.selectFoundClient(foundClients);

            if (!selectedClient) {
                this._console.warn(`search_and_select_client: Usuario canceló la selección para "${trimmedName}"`);
                return undefined;
            }

            this._console.log(`search_and_select_client: Cliente seleccionado exitosamente: ${selectedClient.name} (ID: ${selectedClient.id})`);

            return selectedClient;
        } catch (error) {
            this._llmLoggingService.error(
                `Error buscando y seleccionando cliente: "${trimmedName}"`,
                error,
                { searchName: trimmedName },
                'UtilsMiscellaneousService',
                'search_and_select_client'
            );
            throw error;
        }
    }




    private static readonly CONFIG_FACTORIES: Map<number, ConfigFactory> = new Map([
        [0, new ClientConfigFactory()],
        [1, new OsBudgetConfigFactory()],
    ]);

    /**
     * Obtiene la configuración específica basada en el número de configuración proporcionado.
     *
     * Este método actúa como un factory que recupera y ejecuta la fábrica de configuraciones
     * registrada para el número especificado, retornando la configuración resultante.
     *
     * @param numberConfig - Número identificador de la configuración a obtener.
     *                       Debe corresponder a una configuración registrada en CONFIG_FACTORIES.
     *
     * @returns La instancia de Configuration creada por la fábrica correspondiente.
     *
     * @throws Error - Cuando el numberConfig proporcionado no tiene una fábrica de configuraciones
     *                 registrada en CONFIG_FACTORIES.
     *
     * @example
     * // Obtener configuración para cliente (config 0)
     * const config = UtilsMiscellaneousService.getConfig(0);
     *
     * @example
     * // Esto lanzará un error si la configuración 99 no existe
     * const invalidConfig = UtilsMiscellaneousService.getConfig(99); // Error: Configuración no soportada: 99
     */
    static getConfig(numberConfig: number): Configuration {
        const factory = this.CONFIG_FACTORIES.get(numberConfig);

        if (!factory) {
            throw new Error(`Configuración no soportada: ${numberConfig}`);
        }

        return factory.create();
    }



    async selectFoundClient(clients: Client[]): Promise<Client | undefined> {
        if (!clients || clients.length === 0) {
            this._console.warn('selectFoundClient: Lista de clientes vacía o nula');
            return undefined;
        }

        const selectConfig = UtilsMiscellaneousService.getConfig(0);

        const subArrayToObj: PropertiesDialogDataSelect = {
            items: clients.map(dat => ({
                id: dat.id,
                name: dat.name,
                descriptor: dat.document,
            })),
            ...selectConfig
        }

        try{

            const selectedId: string = await firstValueFrom(
                this._toolsAiDialogsService.openDataSelectionDialog(subArrayToObj)
            );

            if (!selectedId) {
                this._console.warn('selectFoundClient: No se seleccionó ningún cliente');
                return undefined;
            }

            const client = clients.find(client => client.id === selectedId);

            if (!client) {
                this._llmLoggingService.error(
                    `Cliente seleccionado no encontrado en la lista original`,
                    { selectedId, availableClients: clients.map(c => c.id) },
                    'UtilsMiscellaneousService',
                    'selectFoundClient'
                );
                return undefined;
            }

            return client;

        } catch (e) {
            this._llmLoggingService.error(
                'Error al seleccionar cliente',
                e,
                { clientsCount: clients.length },
                'UtilsMiscellaneousService',
                'selectFoundClient'
            );
            throw e;
        }
    }

    async selectFoundOsBudget(os_budgets: MkgOS[]): Promise<MkgOS | undefined> {
        if (!os_budgets || os_budgets.length === 0) {
            this._console.warn('selectFoundOsBudget: Lista de os_budgets vacía o nula');
            return undefined;
        }

        const selectConfig = UtilsMiscellaneousService.getConfig(1);

        const subArrayToObj: PropertiesDialogDataSelect = {
            items: os_budgets.map(dat => ({
                id: dat.id,
                name: dat.clientTmp,
                descriptor: dat.budgetCode.toString(),
            })),
            ...selectConfig
        }

        try{

            const selectedId: string = await firstValueFrom(
                this._toolsAiDialogsService.openDataSelectionDialog(subArrayToObj)
            );

            if (!selectedId) {
                this._console.warn('selectFoundOsBudget: No se seleccionó ningún os_budget');
                return undefined;
            }

            const os_budget = os_budgets.find(client => client.id === selectedId);

            if (!os_budget) {
                this._llmLoggingService.error(
                    `os_budget seleccionado no encontrado en la lista original`,
                    { selectedId, availableClients: os_budgets.map(c => c.id) },
                    'UtilsMiscellaneousService',
                    'selectFoundOsBudget'
                );
                return undefined;
            }

            return os_budget;

        } catch (e) {
            this._llmLoggingService.error(
                'Error al seleccionar os_budget',
                e,
                { clientsCount: os_budgets.length },
                'UtilsMiscellaneousService',
                'selectFoundOsBudget'
            );
            throw e;
        }
    }



    /**
     * Busca un cliente en la base de datos por nombre.
     * @param name Nombre del cliente a buscar.
     * @returns Una Promesa que se resuelve a un array de `Client` si se encuentran, o un ID de cliente por defecto (string) si no.
     */
    async findClientInDb(name: string): Promise<Client[]> {
        if (!name || name.trim() === '') {
            this._console.warn('Búsqueda de cliente con nombre vacío o inválido.');
            return [];
        }

        const searchParams = { search: name.trim() };

        try {
            const resp = await firstValueFrom(this._clientService.getPaginated(
                searchParams
            ).pipe(
                map(r => r.docs),
                timeout({first: 10000}),
            ));
            return resp ?? [];
        } catch (error: unknown) {
            const isErrorWithStatus = this.isHttpError(error);

            if (isErrorWithStatus && error.status === 400 && error.error?.error === 'PERSON_NOT_FOUND') {
                this._console.warn(`Cliente no encontrado para la búsqueda: "${name}". Devolviendo lista vacía.`);
                return [];
            }

            if (isErrorWithStatus && error.status === 404) {
                this._console.warn(`Cliente no encontrado (404) para la búsqueda: "${name}". Devolviendo lista vacía.`);
                return [];
            }

            this._llmLoggingService.error(
                `Error buscando cliente en DB por nombre: ${name}`,
                error,
                { name, searchParams },
                'RegisterNewOsTool',
                'findClientInDb'
            );

            return [];
        }
    }

    /**
     * Verifica si un error desconocido es un objeto de error HTTP con una propiedad 'status'.
     * Utilizado como type guard para validar la estructura de errores antes de acceder a propiedades específicas.
     * @param error El objeto de error a verificar, puede ser de cualquier tipo.
     * @returns true si el error es un objeto no nulo con una propiedad 'status', false en caso contrario.
     * Este método actúa como un type guard que estrecha el tipo de error a { status: number; error?: any }.
     */
    private isHttpError(error: unknown): error is { status: number; error?: any } {
        return typeof error === 'object' && error !== null && 'status' in error;
    }

}
